import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Image as ImageIcon,
  ImagePlus,
  Loader2,
  Star,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { EditableText } from '@/components/inventory/EditableItemField';
import {
  MAX_ITEM_IMAGE_BYTES,
  sortItemImages,
  useDeleteItemImage,
  useItemImageSignedUrl,
  useItemImages,
  useSetPrimaryItemImage,
  useUpdateItemImageCaption,
  useUploadItemImage,
  type ItemImage,
} from '@/features/inventory/hooks';
import { useAuth } from '@/app/AuthProvider';
import { canWrite, type Role } from '@/lib/permissions';
import { formatDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';

/**
 * Photo gallery for any catalog item: a large view of the selected photo,
 * a thumbnail strip of every view, and a full-screen viewer. The primary
 * photo (`items.image_path`) is shown first and is what list thumbnails
 * render. Editing is gated on `canWrite('inventory')`, mirroring the
 * admin-only RLS on `item_images` and the `item-images` bucket.
 */
export function ItemPhotosCard({
  itemId,
  primaryPath,
}: {
  itemId: string;
  primaryPath: string | null;
}) {
  const { role } = useAuth();
  const canEdit = canWrite(role as Role | null, 'inventory');

  const { data: images, isLoading } = useItemImages(itemId);
  const upload = useUploadItemImage();
  const setPrimary = useSetPrimaryItemImage();
  const remove = useDeleteItemImage();
  const updateCaption = useUpdateItemImageCaption();

  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  const photos = useMemo(() => sortItemImages(images ?? [], primaryPath), [images, primaryPath]);
  const selectedIndex = Math.max(
    0,
    photos.findIndex((p) => p.id === selectedId),
  );
  const selected = photos[selectedIndex] ?? null;

  const step = (delta: number) => {
    if (photos.length === 0) return;
    const next = (selectedIndex + delta + photos.length) % photos.length;
    setSelectedId(photos[next].id);
  };

  const handleFiles = async (list: FileList | File[] | null) => {
    const files = Array.from(list ?? []);
    if (inputRef.current) inputRef.current.value = '';
    if (files.length === 0) return;
    setUploading(true);
    let needsPrimary = !primaryPath;
    let position = (images ?? []).reduce((max, img) => Math.max(max, img.position), -1) + 1;
    let uploaded = 0;
    let lastId: string | null = null;
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`“${file.name}” isn't an image.`);
          continue;
        }
        if (file.size > MAX_ITEM_IMAGE_BYTES) {
          toast.error(`“${file.name}” is over 10 MB.`);
          continue;
        }
        const created = await upload.mutateAsync({
          itemId,
          file,
          position,
          makePrimary: needsPrimary,
        });
        lastId = created.id;
        needsPrimary = false;
        position += 1;
        uploaded += 1;
      }
      if (uploaded > 0) {
        toast.success(uploaded === 1 ? 'Photo added' : `${uploaded} photos added`);
        setSelectedId(lastId);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const makePrimary = async (photo: ItemImage) => {
    try {
      await setPrimary.mutateAsync({ itemId, path: photo.storage_path });
      toast.success('Primary photo updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not set primary photo');
    }
  };

  const deletePhoto = async (photo: ItemImage) => {
    if (!window.confirm('Delete this photo? This can’t be undone.')) return;
    try {
      await remove.mutateAsync({ image: photo, primaryPath });
      if (photo.id === selectedId) setSelectedId(null);
      toast.success('Photo deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete photo');
    }
  };

  const busy = uploading || setPrimary.isPending || remove.isPending;
  const isPrimary = (photo: ItemImage) => photo.storage_path === primaryPath;

  const dropProps = canEdit
    ? {
        onDragOver: (e: React.DragEvent) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDragOver(true);
        },
        onDragLeave: () => setDragOver(false),
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          setDragOver(false);
          void handleFiles(e.dataTransfer.files);
        },
      }
    : {};

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>
            Product photos
            {photos.length > 0 && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {photos.length}
              </span>
            )}
          </CardTitle>
          {canEdit && (
            <>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => void handleFiles(e.target.files)}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ImagePlus className="h-4 w-4" />
                )}
                {uploading ? 'Uploading…' : 'Add photos'}
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          {...dropProps}
          className={cn(
            'relative aspect-square w-full overflow-hidden rounded-md border border-border bg-secondary/30',
            dragOver && 'border-primary ring-2 ring-primary/40',
          )}
        >
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : selected ? (
            <MainPhoto
              itemId={itemId}
              photo={selected}
              primary={isPrimary(selected)}
              onOpen={() => setViewerOpen(true)}
              onPrev={photos.length > 1 ? () => step(-1) : undefined}
              onNext={photos.length > 1 ? () => step(1) : undefined}
            />
          ) : (
            <button
              type="button"
              disabled={!canEdit || busy}
              onClick={() => inputRef.current?.click()}
              className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground enabled:hover:text-foreground"
            >
              <ImageIcon className="h-10 w-10" />
              <span className="text-xs">
                {canEdit ? 'No photos yet — add or drop images here' : 'No photos yet'}
              </span>
            </button>
          )}
        </div>

        {selected && (
          <div className="space-y-1">
            <EditableText
              value={selected.caption ?? ''}
              maxLength={200}
              ariaLabel="Photo caption"
              placeholder="Add a caption (e.g. Front, Side, In use)"
              canEdit={canEdit}
              density="cell"
              triggerClassName="text-sm"
              display={
                selected.caption ? undefined : (
                  <span className="text-muted-foreground">
                    {canEdit ? 'Add a caption (e.g. Front, Side, In use)' : 'No caption'}
                  </span>
                )
              }
              onSave={async (next) => {
                await updateCaption.mutateAsync({ id: selected.id, itemId, caption: next });
              }}
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                Added {formatDate(selected.created_at)}
              </span>
              {canEdit && (
                <div className="flex items-center gap-1">
                  {!isPrimary(selected) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => void makePrimary(selected)}
                      disabled={busy}
                    >
                      <Star className="h-4 w-4" />
                      Make primary
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => void deletePhoto(selected)}
                    disabled={busy}
                    aria-label="Delete photo"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {photos.length > 1 && (
          <div className="grid grid-cols-5 gap-2">
            {photos.map((photo) => (
              <Thumbnail
                key={photo.id}
                itemId={itemId}
                photo={photo}
                primary={isPrimary(photo)}
                selected={photo.id === selected?.id}
                onSelect={() => setSelectedId(photo.id)}
              />
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={viewerOpen && selected != null} onOpenChange={setViewerOpen}>
        <DialogContent className="max-h-[95vh] max-w-4xl overflow-y-auto" dismissOnOutsideClick>
          {selected && (
            <PhotoViewer
              itemId={itemId}
              photo={selected}
              primary={isPrimary(selected)}
              position={`${selectedIndex + 1} of ${photos.length}`}
              onPrev={photos.length > 1 ? () => step(-1) : undefined}
              onNext={photos.length > 1 ? () => step(1) : undefined}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PrimaryBadge() {
  return (
    <Badge variant="success" className="gap-1">
      <Star className="h-3 w-3" />
      Primary
    </Badge>
  );
}

function NavButton({ direction, onClick }: { direction: 'prev' | 'next'; onClick: () => void }) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <Button
      type="button"
      size="icon"
      variant="outline"
      className={cn(
        'absolute top-1/2 -translate-y-1/2 bg-card/90',
        direction === 'prev' ? 'left-2' : 'right-2',
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={direction === 'prev' ? 'Previous photo' : 'Next photo'}
    >
      <Icon className="h-5 w-5" />
    </Button>
  );
}

function MainPhoto({
  itemId,
  photo,
  primary,
  onOpen,
  onPrev,
  onNext,
}: {
  itemId: string;
  photo: ItemImage;
  primary: boolean;
  onOpen: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}) {
  const { data: url, isLoading } = useItemImageSignedUrl(itemId, photo.storage_path);
  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        className="block h-full w-full cursor-zoom-in"
        aria-label="Open photo viewer"
      >
        {isLoading || !url ? (
          <Skeleton className="h-full w-full rounded-none" />
        ) : (
          <img
            src={url}
            alt={photo.caption ?? 'Product'}
            className="h-full w-full object-contain"
          />
        )}
      </button>
      {primary && (
        <div className="pointer-events-none absolute left-2 top-2">
          <PrimaryBadge />
        </div>
      )}
      {onPrev && <NavButton direction="prev" onClick={onPrev} />}
      {onNext && <NavButton direction="next" onClick={onNext} />}
    </>
  );
}

function Thumbnail({
  itemId,
  photo,
  primary,
  selected,
  onSelect,
}: {
  itemId: string;
  photo: ItemImage;
  primary: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const { data: url, isLoading } = useItemImageSignedUrl(itemId, photo.storage_path);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={photo.caption ? `Show ${photo.caption}` : 'Show photo'}
      aria-pressed={selected}
      title={photo.caption ?? undefined}
      className={cn(
        'relative aspect-square overflow-hidden rounded border bg-secondary/30',
        selected
          ? 'border-primary ring-2 ring-primary/40'
          : 'border-border hover:border-foreground/40',
      )}
    >
      {isLoading || !url ? (
        <Skeleton className="h-full w-full rounded-none" />
      ) : (
        <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />
      )}
      {primary && (
        <span className="absolute right-0.5 top-0.5 rounded-full bg-card/90 p-0.5">
          <Star className="h-3 w-3 fill-amber-400 text-amber-500" />
        </span>
      )}
    </button>
  );
}

function PhotoViewer({
  itemId,
  photo,
  primary,
  position,
  onPrev,
  onNext,
}: {
  itemId: string;
  photo: ItemImage;
  primary: boolean;
  position: string;
  onPrev?: () => void;
  onNext?: () => void;
}) {
  const { data: url, isLoading } = useItemImageSignedUrl(itemId, photo.storage_path);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') onPrev?.();
      if (e.key === 'ArrowRight') onNext?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onPrev, onNext]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 pr-8">
        <DialogTitle className="truncate">{photo.caption || 'Product photo'}</DialogTitle>
        {primary && <PrimaryBadge />}
      </div>
      <DialogDescription>
        {position} · Added {formatDate(photo.created_at)}
      </DialogDescription>
      <div className="relative flex min-h-[40vh] items-center justify-center rounded-md bg-secondary/30">
        {isLoading || !url ? (
          <Skeleton className="h-[60vh] w-full" />
        ) : (
          <img
            src={url}
            alt={photo.caption ?? 'Product'}
            className="max-h-[70vh] w-auto object-contain"
          />
        )}
        {onPrev && <NavButton direction="prev" onClick={onPrev} />}
        {onNext && <NavButton direction="next" onClick={onNext} />}
      </div>
      {url && (
        <div className="flex justify-end">
          <Button asChild variant="outline" size="sm">
            <a href={url} target="_blank" rel="noreferrer" download>
              <Download className="h-4 w-4" />
              Download
            </a>
          </Button>
        </div>
      )}
    </div>
  );
}
